/* eslint-disable prefer-const */
import { useEffect, useMemo, useState, useCallback } from "react";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { IAircraftFlights } from "src/redux/reducers/FlightTimelineReducer";

// Styling
import { useTheme } from "@mui/styles";
import { Grid, Typography, styled } from "@mui/material";
import { FlightGroupChip } from "./FlightGroupChip";
import { IFlight, IAircraft, unassignedAircraftId, IFullAircraftConfiguration } from "@vmosolutions/oc-interfaces";
import { selectByFlightNumberPredicate, selectByPredicate } from "./FlightContextMenu";

// Redux Actions
import { getUnassignedAircraftFlights, updateSelectedFlight } from "src/redux/actions/flights/FlightData.Actions";
// Manage Aircraft Configurations
import { convertAircraftToFlightAircraft } from "src/redux/reducers/FlightTimelineReducer";

interface IDayContextMenuProps {
  selectedDay: Date;
  selectedAircraftId: string;
}

const OCDayContextMenu = styled(Grid)(({ theme }) => ({
  margin: "15px 0px",
  padding: 10,
  border: `1px solid ${theme.custom.timeline.contextListBackground}`,
  "& .unassigned_flights_info": {
    "& .date": { color: theme.palette.primary.contrastText },
    "& .aircraft": { color: theme.custom.timeline.groupSelected },
  },
  "& .flight_groups_container": {
    margin: "10px 0px",
    position: "relative",
  },
}));

/**
 * Filter Unassigned flights 'scheduled Departure' by currently Selected Day.
 * For now this is using UTC time but will change to assigned local time for user.
 */
export const filteredUnassignedFlights = (unassignedFlights: IFlight[], s: Date, aircraftFlights: any): any => {
  const selectedDay = `${s.getUTCFullYear()}-${s.getUTCMonth()}-${s.getUTCDate()}`;
  const unassignedFlightsOnDay = unassignedFlights.filter((flight: IFlight) => {
    const f = new Date(flight.scheduledOff);
    const flightDay = `${f.getUTCFullYear()}-${f.getUTCMonth()}-${f.getUTCDate()}`;
    return flightDay === selectedDay;
  });

  // Group all flights together based on their flight numbers (to create flightGroupChips)
  const newFlightGroupings = unassignedFlightsOnDay.reduce((group: any, flight: IFlight) => {
    if (group.hasOwnProperty(flight.flightNumber)) {
      group[flight.flightNumber].push(flight); // Add to group.
    } else {
      group[flight.flightNumber] = [flight]; // Create new group.
    }
    return group;
  }, {});

  for (let groupKey in newFlightGroupings) {
    const flightGroup = newFlightGroupings[groupKey];
    const flightInGroup = flightGroup[0];
    // Get any flights outside of the selected day that may belong to this flight group.
    const completeFlightGroup = selectByPredicate(flightInGroup, aircraftFlights, selectByFlightNumberPredicate);

    // The selectByPredicate removed overlapping flights. If missing IDs are found, re-add them to the flight group.
    let i: number,
      len: number = flightGroup.length;
    for (i = 0; i < len; i++) {
      const flightA = flightGroup[i];
      if (!completeFlightGroup.find((flightB: IFlight) => flightB._id === flightA._id)) {
        completeFlightGroup.push(flightA);
      }
    }

    const firstFlightInGroup = completeFlightGroup[0];
    const f = new Date(firstFlightInGroup.scheduledOff);
    const firstFlightDay = `${f.getUTCFullYear()}-${f.getUTCMonth()}-${f.getUTCDate()}`;
    // If the first flight in the group departed on the selected day, include the flightGroup object
    // Otherwise remove it.
    if (firstFlightDay === selectedDay) {
      newFlightGroupings[groupKey] = completeFlightGroup;
    } else {
      delete newFlightGroupings[groupKey];
    }
  }
  return newFlightGroupings;
};

/////////////////////////////////////////////////////
///////  UNNASSIGNED FLIGHTS / FLIGHT GROUPS
/////////////////////////////////////////////////////

export const DayContextMenu = (props: IDayContextMenuProps): JSX.Element => {
  const dispatch = useDispatch();
  const aircraftFlights: IAircraftFlights = useSelector((state: IRootState) => state.timelineFlights.flightData);

  const unassignedAircraft = aircraftFlights[unassignedAircraftId];
  const unassignedFlights = unassignedAircraft.flights;
  const [flightGroups, setFlightGroups] = useState<any>(null);
  const [selectionDate, setSelectionDate] = useState<string>(null);
  const [selectedAircraftDefaultConfig, setSelectedAircraftDefaultConfig] = useState<IFullAircraftConfiguration>(null);
  const theme = useTheme();

  const { selectedDay, selectedAircraftId } = props;
  const allAircrafts = useSelector((state: IRootState) => state.structures.customerStructure.aircraft);

  const selectedAircraft = useMemo(() => {
    const aircraft = allAircrafts.find((AC: IAircraft) => AC._id === selectedAircraftId);
    setSelectedAircraftDefaultConfig(aircraft?.defaultConfig);
    return aircraft?._id ? convertAircraftToFlightAircraft(aircraft) : null;
  }, [selectedAircraftId, allAircrafts]);

  // Retrieve all available unassigned flights. (testing)
  useEffect(() => {
    if (!unassignedAircraft.lastProcessedTime) void dispatch(getUnassignedAircraftFlights());
  }, [unassignedAircraft.lastProcessedTime, dispatch]);

  // If either the selectedDay, selectedAircraftId or unassignedFlights data changes, refilter the available chips.
  // NOTE: This seems to be firing off more often than it should (investigate).
  useEffect(() => {
    if (!!unassignedFlights && !!selectedAircraftId && !!selectedDay) {
      const d = selectedDay;
      setSelectionDate(`${d.toUTCString().split(" ").slice(0, 4).join(" ")}`);
      setFlightGroups(filteredUnassignedFlights(unassignedFlights, d, aircraftFlights));
    }
  }, [selectedAircraftId, selectedDay, unassignedFlights, aircraftFlights]);

  /** Handle Assignment of flight groups */
  const assignFlightGroup = useCallback(
    (flights: IFlight[]): void => {
      if (!flights?.length || !selectedAircraft) {
        return;
      }
      flights.map((flight: IFlight, index: number) => {
        const payload = { ...{ aircraft: selectedAircraft }, ...{ updateType: "ReassignAircraft" } };
        dispatch(updateSelectedFlight(flight._id, payload));
        return false;
      });
    },
    [dispatch, selectedAircraft]
  );

  /** Handle Assignment of individual flights */
  const assignIndividualFlight = useCallback(
    (flightId: string): void => {
      const flight = unassignedFlights.find((flight: IFlight) => flight._id === flightId);
      if (!flight || !selectedAircraft) {
        return;
      }
      const payload = { ...{ aircraft: selectedAircraft }, ...{ updateType: "ReassignAircraft" } };
      dispatch(updateSelectedFlight(flight._id, payload));
    },
    [dispatch, selectedAircraft, unassignedFlights]
  );

  /**
   * Render the flightGroupChips
   */
  const renderFlightGroups = useMemo((): JSX.Element[] => {
    if (!flightGroups) {
      return null;
    }
    let flightGroupChips: JSX.Element[] = [];
    for (let flightGroupKey in flightGroups) {
      const flightGroup: IFlight[] = flightGroups[flightGroupKey];
      if (Array.isArray(flightGroup) && flightGroup.length > 0) {
        flightGroupChips.push(
          <FlightGroupChip
            data={flightGroup}
            onClickGroup={selectedAircraft ? () => assignFlightGroup(flightGroup) : null}
            onClickChip={selectedAircraft ? assignIndividualFlight : null}
            groupKey={flightGroupKey}
            key={flightGroupKey}
            buttonAlignRight={true}
          />
        );
      }
    }
    return flightGroupChips;
  }, [flightGroups, assignIndividualFlight, assignFlightGroup, selectedAircraft]);

  return (
    <OCDayContextMenu container direction="column">
      <Grid item className="unassigned_flights_info">
        {!!flightGroups && Object.keys(flightGroups).length > 0 ? (
          <Typography variant="body2">
            Unassigned flight groups scheduled to depart <span className="date">{selectionDate}</span>. To Assign to
            Aircraft (<span className="aircraft">{selectedAircraft?.registration}</span>), click on collapsed flight
            group to assign all or expand to assign individual flights.
          </Typography>
        ) : (
          <Typography variant="body2">
            No unassigned flights scheduled for <span className="date">{selectionDate}</span>.
          </Typography>
        )}
      </Grid>
      <Grid item className="flight_groups_container">
        {renderFlightGroups}
      </Grid>
    </OCDayContextMenu>
  );
};
